import React, {
  ComponentProps,
  CSSProperties,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useQuery, useMutation } from '@apollo/client';
import Spinner from '../../ui/Spinner/Spinner';
import Panel from '../../ui/Panel/Panel';
import PanelHeader from '../../ui/PanelHeader/PanelHeader';
import {
  useAnalyticsPost,
  NarvarEventTypes,
  NarvarEventComponents,
} from '../../../hooks/Analytics';
import {
  usePageContext,
  LineStatus,
  useTitanAnalytics,
} from '../../../hooks/PageContext/usePageContext';
import { useSettings } from '../../../hooks/useSettings';
import {
  NotificationPrefDetails,
  Channel,
  RetailerTheme,
} from '../../../../../__generated__/loma';
import {
  getNotificationPreferencesAndLevelQuery,
  getUserPrefsNotificationPreferences,
  subscribeUserPrefsNotificationPreferencesMutation,
  unsubscribeUserPrefsNotificationPreferencesMutation,
} from './graphql';
import { useTranslations } from '../../../hooks/useTranslations';
import { LayoutTree } from '../DynamicLayout';
import { UI_STATE, Channels, PreferenceLevels } from './types';
import OptIn from './components/OptIn';
import Subscribed from './components/Subscribed';
import Unsubscribed from './components/Unsubscribed';
import ErrorAPI from './components/ErrorAPI/ErrorAPI';
import { useTheme } from '../../../hooks/useTheme';
import { CprefMarketing } from './components/CprefMarketing';
import { useGlobalSettings } from '../../../hooks/useGlobalSettings';
import { useSubscribeNotificationPreferenceMutate } from './hooks/useSubscribeNotificationPreferenceMutate';
import { useUnsubscribeNotificationPreferenceMutate } from './hooks/useUnsubscribeNotificationPreferenceMutate';
import ConfirmPhoneNumber from './components/ConfirmPhoneNumber';
import { useConsumerPreferences } from './hooks/useConsumerPreferences';
import { useIsTrackFeatureOn } from '../../../hooks/useGrowthbook';

export * from './types';

const { FeatureLayout } = LayoutTree;

export enum ERROR_TYPES {
  API_ERROR = 'API_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  SUBLIME_VALIDATION_ERROR = 'SUBLIME_VALIDATION_ERROR',
}

export interface NotificationCenterProps {
  featureName?: string;
  layout?: LayoutTree.FeatureLayout;
  fixedPanelHeight?: string;
  channel: Channels;
}

export interface NotificationChannelProps {
  panelTitleText: string;
  eventComponent: string;
}

const NotificationCenter = ({
  featureName,
  layout = FeatureLayout.PORTRAIT,
  fixedPanelHeight,
  channel,
}: NotificationCenterProps) => {
  const { state: pageContext } = usePageContext();
  const {
    experienceHost,
    embedReferrerUrl,
    requestUrl,
    lineStatus,
    retailerMoniker,
    product,
    trackingNumbers,
    orderNumbers,
    carrier,
    narvarProfileUrl = 'https://profile.narvar.com',
  } = pageContext;

  const { enabled: sublimeDeliveryNotifications } = useIsTrackFeatureOn(
    'sublime-delivery-notifications',
  );

  const [notificationsUIState, setNotificationsUIState] = useState<UI_STATE>(
    UI_STATE.OPT_IN,
  );
  const [validationError, setValidationError] = useState<ERROR_TYPES>();
  const [customApiErrorMessage, setCustomApiErrorMessage] = useState<string>();
  const [contact, setContact] = useState('');
  const [cprefOptedIn, setCprefOptedIn] = useState(false);

  const { notificationCenter: notificationCenterSettings } = useSettings();

  const {
    marketingOptInEnabled,
    marketingOptInPrivacyLink,
    marketingOptInTermsLink,
  } = notificationCenterSettings ?? {};

  const { hipaaCompliant, retailerName } = useGlobalSettings();

  const trackingNumber = (trackingNumbers && trackingNumbers[0]) ?? '';
  // in the context of messaging API, orderNumber is called orderId
  const orderId = (orderNumbers && orderNumbers[0]) ?? '';
  const referrerUrl =
    experienceHost === 'EMBED' && embedReferrerUrl
      ? embedReferrerUrl
      : (requestUrl ?? window?.location.href);

  const { optinEnabled: consumerPreferencesOptinEnabled } =
    useConsumerPreferences();

  const {
    notificationCenterSmsPanelTitleText,
    notificationCenterEmailPanelTitleText,
    notificationCenterAppPushPanelTitleText,
    notificationCenterLinePanelTitleText,
  } = useTranslations();

  // Temporary Translation
  const notificationCenterGenericApiError =
    'Sign up for delivery notifications is currently unavailable at this time.  Please try again later.';

  const { spinnerPrimaryNone: spinnerStyle } = useTheme() as {
    [key in keyof RetailerTheme]: CSSProperties;
  };

  const smsNotificationChannelProps: NotificationChannelProps = {
    panelTitleText: notificationCenterSmsPanelTitleText ?? '',
    eventComponent: NarvarEventComponents.NTH_PACK_NOTIFICATION_CENTER,
  };

  const emailNotificationChannelProps: NotificationChannelProps = {
    panelTitleText: notificationCenterEmailPanelTitleText ?? '',
    eventComponent: NarvarEventComponents.NTH_PACK_EMAIL_NOTIFICATION_CENTER,
  };

  const appPushNotificationChannelProps: NotificationChannelProps = {
    panelTitleText: notificationCenterAppPushPanelTitleText ?? '',
    eventComponent: NarvarEventComponents.NTH_PACK_APP_PUSH_NOTIFICATION_CENTER,
  };

  const lineNotificationChannelProps: NotificationChannelProps = {
    panelTitleText: notificationCenterLinePanelTitleText ?? '',
    eventComponent: NarvarEventComponents.NTH_PACK_LINE_NOTIFICATION_CENTER,
  };

  const notificationCenterChannelProps = {
    [Channels.SMS]: smsNotificationChannelProps,
    [Channels.HIPAA_SMS]: smsNotificationChannelProps,
    [Channels.EMAIL]: emailNotificationChannelProps,
    [Channels.HIPAA_EMAIL]: emailNotificationChannelProps,
    [Channels.APP_PUSH]: appPushNotificationChannelProps,
    [Channels.LINE]: lineNotificationChannelProps,
    [Channels.SUBLIME]: smsNotificationChannelProps,
  };

  const { panelTitleText, eventComponent } =
    notificationCenterChannelProps[channel];

  const {
    data: getUserPrefsNotificationPreferencesData,
    loading: getUserPrefsNotificationPreferencesLoading,
  } = useQuery(getUserPrefsNotificationPreferences, {
    skip: sublimeDeliveryNotifications === false,
    variables: {
      retailerMoniker,
      carrier,
      orderNumber: orderId,
      trackingNumber,
    },
    onError: () => {
      setNotificationsUIState(UI_STATE.ERROR_API);
      setCustomApiErrorMessage(notificationCenterGenericApiError);
    },
  });

  const { data: getNotificationPreferencesAndLevelData } = useQuery(
    getNotificationPreferencesAndLevelQuery,
    {
      variables: {
        ...pageContext,
        hipaaCompliant,
        referrerUrl,
      },
    },
  );

  const notificationPreferences =
    getNotificationPreferencesAndLevelData?.getNotificationPreferencesAndLevel ??
    undefined;

  const retailerMarketingOptinEnabled =
    marketingOptInEnabled &&
    marketingOptInPrivacyLink !== undefined &&
    marketingOptInTermsLink !== undefined &&
    notificationPreferences?.preferenceLevel !== PreferenceLevels.RETAILER;

  const titan = useTitanAnalytics();
  const logEvent = useAnalyticsPost();

  const baseLogInfo = useMemo(
    () => ({
      eventComponent,
      eventFeature: eventComponent,
      feature: featureName,
      orderNumber: orderId,
      pageComponent: eventComponent,
      pageOrderNumber: orderId,
    }),
    [eventComponent, featureName, orderId],
  );

  const isAppleWallet =
    notificationPreferences?.preferenceLevel === 'ORDER_ONLY';

  const currentChannel =
    (isAppleWallet && Channels.APP_PUSH) ||
    (hipaaCompliant && channel === Channels.EMAIL && Channels.HIPAA_EMAIL) ||
    (hipaaCompliant && channel === Channels.SMS && Channels.HIPAA_SMS) ||
    channel;

  const isSubscribedToCurrentChannel =
    notificationPreferences &&
    (notificationPreferences.notificationPrefDetails ?? []).some(
      (subscribedChannel: NotificationPrefDetails): boolean =>
        (subscribedChannel && subscribedChannel.channel === currentChannel) ??
        false,
    );

  // TODO: Improve this logic to handle multiple opt-ins
  const sublimeSubscribed =
    getUserPrefsNotificationPreferencesData?.getUserPrefsNotificationPreferences
      ?.optIns?.[0]?.preferences[0]?.status === 'active';

  const { profileId } =
    getUserPrefsNotificationPreferencesData?.getUserPrefsNotificationPreferences ??
    {};

  useEffect(() => {
    if (isSubscribedToCurrentChannel || isAppleWallet) {
      setNotificationsUIState(UI_STATE.SUBSCRIBED);
    }
  }, [isSubscribedToCurrentChannel, isAppleWallet]);

  useEffect(() => {
    if (sublimeSubscribed) {
      setNotificationsUIState(UI_STATE.CPREF_SUBSCRIBED);
    }
  }, [sublimeSubscribed]);

  useEffect(() => {
    if (
      (sublimeSubscribed || isSubscribedToCurrentChannel) &&
      consumerPreferencesOptinEnabled &&
      retailerMarketingOptinEnabled
    ) {
      setNotificationsUIState(UI_STATE.CPREF_MARKETING);
    }
  }, [
    sublimeSubscribed,
    isSubscribedToCurrentChannel,
    consumerPreferencesOptinEnabled,
    retailerMarketingOptinEnabled,
  ]);

  useEffect(() => {
    if (lineStatus?.toUpperCase() === LineStatus.FAILURE) {
      setNotificationsUIState(UI_STATE.ERROR_API);
    }
  }, [lineStatus]);

  useEffect(() => {
    if (
      consumerPreferencesOptinEnabled &&
      getUserPrefsNotificationPreferencesLoading === false
    ) {
      titan
        ?.track({
          event: 'product_viewed',
          userId: profileId,
          channel: 'web',
          type: 'track',
          context: {},
          properties: {
            sku: 'Notification Center',
            variant: sublimeSubscribed === true ? 'subscribed' : 'unsubscribed',
            narvar_product_context: 'track',
            extra_properties: [
              {
                key: 'orderId',
                value: orderId,
              },
              {
                key: 'trackingNumber',
                value: trackingNumber,
              },
            ],
          },
        })
        // eslint-disable-next-line no-console
        .catch((e) => console.error(e));
    }
  }, [
    titan,
    orderId,
    trackingNumber,
    getUserPrefsNotificationPreferencesLoading,
    sublimeSubscribed,
    consumerPreferencesOptinEnabled,
    profileId,
  ]);

  useEffect(() => {
    if (consumerPreferencesOptinEnabled === false) return;

    titan
      ?.track({
        event: 'promotion_viewed',
        channel: 'web',
        type: 'track',
        context: {},
        properties: {
          promotion_id: 'notification-preferences',
          narvar_product_context: 'track',
          extra_properties: [
            {
              key: 'orderId',
              value: orderId,
            },
            {
              key: 'trackingNumber',
              value: trackingNumber,
            },
          ],
        },
      })
      // eslint-disable-next-line no-console
      .catch((e) => console.error(e));
  }, [titan, consumerPreferencesOptinEnabled, orderId, trackingNumber]);

  useEffect(() => {
    logEvent({
      ...baseLogInfo,
      eventType: NarvarEventTypes.COMPONENT_RENDER,
    });
  }, [baseLogInfo, logEvent]);

  // Notification preferences opt-in
  const { onSubscribeMutate, loading: subscribeLoading } =
    useSubscribeNotificationPreferenceMutate({
      data: getNotificationPreferencesAndLevelData,
      options: {
        onError: (error) => {
          if (error && error.message.includes(ERROR_TYPES.VALIDATION_ERROR)) {
            setValidationError(ERROR_TYPES.VALIDATION_ERROR);
            logEvent({
              ...baseLogInfo,
              eventNotificationSmsValidationError: `${
                error.message
              }; page trackingNumber: ${trackingNumbers?.toString() || ''}`,
              eventType: NarvarEventTypes.ERROR,
            });
            return;
          }

          setNotificationsUIState(UI_STATE.ERROR_API);
        },
        onCompleted: () => {
          if (
            consumerPreferencesOptinEnabled &&
            retailerMarketingOptinEnabled
          ) {
            setNotificationsUIState(UI_STATE.CPREF_MARKETING);
            return;
          }

          setNotificationsUIState(UI_STATE.SUBSCRIBED);
        },
      },
    });

  // Notification preferences opt-out
  const { onUnsubscribeMutate, loading: unsubscribeLoading } =
    useUnsubscribeNotificationPreferenceMutate({
      data: getNotificationPreferencesAndLevelData,
      options: {
        onError: () => {
          setNotificationsUIState(UI_STATE.ERROR_API);
        },
        onCompleted: () => {
          setNotificationsUIState(UI_STATE.UNSUBSCRIBED);
        },
      },
    });

  // Consumer notification preferences opt-in
  const [
    subscribeUserPrefsNotificationPreferences,
    { loading: userPrefsSubscribeLoading },
  ] = useMutation(subscribeUserPrefsNotificationPreferencesMutation, {
    refetchQueries: [getUserPrefsNotificationPreferences],
    awaitRefetchQueries: true,
    onError: (error) => {
      const phoneNumberValidationError = error.graphQLErrors.find(
        (error) =>
          error.extensions &&
          error.extensions.code === ERROR_TYPES.SUBLIME_VALIDATION_ERROR,
      );

      // NOTE: Temporary check to allow us to deploy sutro/loma separately
      const sublimeValidationError =
        error.message === ERROR_TYPES.SUBLIME_VALIDATION_ERROR;

      if (phoneNumberValidationError || sublimeValidationError) {
        setValidationError(ERROR_TYPES.SUBLIME_VALIDATION_ERROR);
        logEvent({
          ...baseLogInfo,
          eventNotificationSmsValidationError: error.message,
          eventType: NarvarEventTypes.ERROR,
        });
        return;
      }

      setNotificationsUIState(UI_STATE.ERROR_API);
    },
    onCompleted: () => {
      if (retailerMarketingOptinEnabled && consumerPreferencesOptinEnabled) {
        setNotificationsUIState(UI_STATE.CPREF_MARKETING);
        return;
      }

      setNotificationsUIState(
        !orderId && consumerPreferencesOptinEnabled
          ? UI_STATE.CPREF_CONFIRM
          : UI_STATE.CPREF_SUBSCRIBED,
      );
    },
  });

  // Consumer notification preferences opt-out
  const [
    unsubscribeUserPrefsNotificationPreferences,
    { loading: userPrefsUnsubscribeLoading },
  ] = useMutation(unsubscribeUserPrefsNotificationPreferencesMutation, {
    refetchQueries: [getUserPrefsNotificationPreferences],
    awaitRefetchQueries: true,
    onError: () => {
      // TODO: Should log something
      setNotificationsUIState(UI_STATE.ERROR_API);
    },
    onCompleted: () => {
      titan
        ?.track({
          event: 'promotion_clicked',
          channel: 'web',
          type: 'track',
          context: {},
          properties: {
            promotion_id: 'notification-preferences',
            narvar_product_context: 'track',
            extra_properties: [
              {
                key: 'action',
                value: 'unsubscribe',
              },
              {
                key: 'orderId',
                value: orderId,
              },
              {
                key: 'trackingNumber',
                value: trackingNumber,
              },
            ],
          },
        })
        // eslint-disable-next-line no-console
        .catch((e) => console.error(e));

      setNotificationsUIState(UI_STATE.CPREF_UNSUBSCRIBED);
    },
  });

  const handleOptin = async (
    contact: string,
    retailerLevelChecked: boolean,
    marketingOptInChecked?: boolean,
    saveNotificationPreferencesChecked?: boolean,
  ) => {
    const notificationPrefDetails = [
      { channel: currentChannel, contact },
    ] satisfies NotificationPrefDetails[];

    setCprefOptedIn(saveNotificationPreferencesChecked ?? false);

    if (retailerMarketingOptinEnabled) setContact(contact);

    if (saveNotificationPreferencesChecked) {
      const data = await subscribeUserPrefsNotificationPreferences({
        variables: {
          retailerMoniker,
          notificationPrefDetails,
          orderNumber: orderId,
          trackingNumber,
          carrier,
          lineageMetadata: {
            retailerMoniker,
            product,
            retailerBrand: retailerName ?? retailerMoniker,
            orderNumber: orderId ?? '',
            trackingNumber,
          },
        },
      });

      const profileId =
        data.data?.subscribeUserPrefsNotificationPreferences?.profileId;

      titan
        ?.track({
          event: 'promotion_clicked',
          userId: profileId,
          channel: 'web',
          type: 'track',
          context: {},
          properties: {
            promotion_id: 'notification-preferences',
            narvar_product_context: 'track',
            extra_properties: [
              {
                key: 'action',
                value: 'subscribe',
              },
              {
                key: 'orderId',
                value: orderId,
              },
              {
                key: 'trackingNumber',
                value: trackingNumber,
              },
            ],
          },
        })
        // eslint-disable-next-line no-console
        .catch((e) => console.error(e));

      return;
    }

    onSubscribeMutate(
      currentChannel,
      notificationPrefDetails,
      retailerLevelChecked,
      marketingOptInChecked,
    );
  };

  const unsubscribeFromConsumerPreferencesNotifications = async () => {
    await unsubscribeUserPrefsNotificationPreferences({
      variables: {
        retailerMoniker,
        orderNumber: orderId,
        trackingNumber,
        carrier,
      },
    });
  };

  const unsubscribeFromNotifications = () => {
    onUnsubscribeMutate(currentChannel);
  };

  const switchToOptInUI = () => {
    setValidationError(undefined);
    setNotificationsUIState(UI_STATE.OPT_IN);
  };

  const goToUserPreferencesProfile = () => {
    window.open(`${narvarProfileUrl}/login?type=phone`);
  };

  const handleOptinFormChange = () => {
    setValidationError(undefined);
  };

  const handleCprefExit = () => {
    setNotificationsUIState(UI_STATE.OPT_IN);
  };

  const isLoading =
    subscribeLoading ||
    unsubscribeLoading ||
    userPrefsSubscribeLoading ||
    userPrefsUnsubscribeLoading;

  return (
    <Panel
      fixedPanelHeight={fixedPanelHeight}
      header={<PanelHeader>{panelTitleText}</PanelHeader>}
      sOuter={{
        textAlign: layout === FeatureLayout.PORTRAIT ? 'center' : undefined,
      }}
      sInner={{
        maxWidth: layout === FeatureLayout.PORTRAIT ? '19.25rem' : 'auto',
        margin: '0 auto',
      }}
    >
      {isLoading ? (
        <div data-test="notificationCenterSpinner">
          <Spinner s={spinnerStyle} />
        </div>
      ) : (
        <>
          {notificationsUIState === UI_STATE.OPT_IN && (
            <OptIn
              channel={currentChannel}
              validationError={validationError}
              notificationPreferencesState={notificationPreferences}
              consumerPreferencesOptinEnabled={consumerPreferencesOptinEnabled}
              onOptIn={handleOptin}
              onChange={handleOptinFormChange}
              featureName={featureName}
              layout={layout}
              retailerMarketingOptinEnabled={
                retailerMarketingOptinEnabled ?? undefined
              }
            />
          )}
          {notificationsUIState === UI_STATE.SUBSCRIBED && (
            <Subscribed
              channel={currentChannel}
              notificationPreferencesState={notificationPreferences}
              onUpdateContactInfo={switchToOptInUI}
              onUnsubscribe={unsubscribeFromNotifications}
              featureName={featureName}
              layout={layout}
            />
          )}
          {notificationsUIState === UI_STATE.CPREF_CONFIRM && (
            <ConfirmPhoneNumber
              featureName={featureName}
              isLandscape={layout === FeatureLayout.LANDSCAPE}
              narvarProfileUrl={narvarProfileUrl}
            />
          )}
          {notificationsUIState === UI_STATE.CPREF_SUBSCRIBED && (
            <Subscribed
              channel={Channels.SUBLIME}
              notificationPreferencesState={notificationPreferences}
              onUpdateContactInfo={goToUserPreferencesProfile}
              onUnsubscribe={unsubscribeFromConsumerPreferencesNotifications}
              featureName={featureName}
              layout={layout}
            />
          )}
          {notificationsUIState === UI_STATE.CPREF_UNSUBSCRIBED && (
            <Unsubscribed
              channel={Channels.SUBLIME}
              notificationPreferencesState={notificationPreferences}
              onOptInAgain={switchToOptInUI}
              featureName={featureName}
              layout={layout}
              narvarProfileUrl={narvarProfileUrl}
            />
          )}
          {notificationsUIState === UI_STATE.UNSUBSCRIBED && (
            <Unsubscribed
              channel={currentChannel}
              notificationPreferencesState={notificationPreferences}
              onOptInAgain={switchToOptInUI}
              featureName={featureName}
              layout={layout}
            />
          )}
          {notificationsUIState === UI_STATE.CPREF_MARKETING && (
            <CprefMarketing
              featureName={featureName}
              isLandscape={layout === FeatureLayout.LANDSCAPE}
              contact={contact}
              cprefOptedIn={cprefOptedIn}
              cprefSubscribed={sublimeSubscribed}
              narvarProfileUrl={narvarProfileUrl}
              getNotificationPreferencesAndLevelData={
                getNotificationPreferencesAndLevelData
              }
              onCprefExit={handleCprefExit}
            />
          )}
          {notificationsUIState === UI_STATE.ERROR_API && (
            <ErrorAPI
              channel={currentChannel}
              layout={layout}
              featureName={featureName}
              customApiErrorMessage={customApiErrorMessage}
            />
          )}
        </>
      )}
    </Panel>
  );
};

const NotificationCenterWithChannel = (
  props: Omit<ComponentProps<typeof NotificationCenter>, 'channel'> & {
    channel?: Channels;
  },
) => {
  const channelSettingMap: { [K in Channel]: Channels } = {
    sms: Channels.SMS,
    email: Channels.EMAIL,
    line: Channels.LINE,
  };
  const { notificationCenter: notificationCenterSettings } = useSettings();
  const { channel: channelSetting } = notificationCenterSettings || {};

  const channel =
    props.channel ||
    (channelSetting ? channelSettingMap[channelSetting] : null);

  if (!channel) return null;

  return <NotificationCenter {...props} channel={channel} />;
};

export default NotificationCenterWithChannel;
